import { Grid } from "@mui/material";
import { useCallback } from "react";
import type { FilterWidget, Widgets } from "../../../../../api/types/dashboardsTypes";
import { useDashboardContext } from "../../contexts/DashboardContext";
import { isFilterWidget, isSmartViewWidget } from "../../models/isTileObjectSmartView";
import { fadeInScale } from "../../widgets/filter/settings/fadeInScale";
import FilterWidgetSettingsComponent from "../../widgets/filter/settings/FilterWidgetSettingsComponent";
import SmartViewWidgetSettingsComponent from "../../widgets/smartview/SmartViewWidgetSettingsComponent";
import { isSupportedWidget } from "./isSupportedWidget";

const WidgetSettings = () => {
  const { selectedWidget } = useDashboardContext();

  const isWidgetSelected = selectedWidget && isSupportedWidget(selectedWidget);

  return (
    <Grid
      container
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 10,
        backgroundColor: "background.paper",
        opacity: isWidgetSelected ? 1 : 0,
        visibility: isWidgetSelected ? "visible" : "hidden",
        transition: "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
        animation: isWidgetSelected ? `${fadeInScale} 0.3s ease-in-out` : "none",
      }}
    >
      <WidgetSettingsContent widget={selectedWidget} />
    </Grid>
  );
};

const WidgetSettingsContent = ({ widget }: { widget: Widgets | undefined }) => {
  const { updateWidget, revalidateFilterTree, revalidateChildFilters, filters, dashboardLoaded } =
    useDashboardContext();

  const handleSave = useCallback(
    (widgetId: string, properties: Partial<Widgets>) => {
      updateWidget(widgetId, properties);
    },
    [updateWidget]
  );

  const handleFilterSave = useCallback(
    (widgetId: string, properties: Partial<FilterWidget>, values: unknown[]) => {
      updateWidget(widgetId, properties);
      const isSameNarrowing =
        widget !== undefined &&
        isFilterWidget(widget) &&
        properties.parentWidgetId === widget.parentWidgetId &&
        properties.dashboardFilterId === widget.dashboardFilterId;
      if (isSameNarrowing) {
        void revalidateChildFilters(widgetId, values);
        return;
      }
      void revalidateFilterTree(
        widgetId,
        { parentWidgetId: properties.parentWidgetId, dashboardFilterId: properties.dashboardFilterId },
        values
      );
    },
    [revalidateChildFilters, revalidateFilterTree, updateWidget, widget]
  );

  if (!widget || !isSupportedWidget(widget)) {
    return null;
  }

  if (isSmartViewWidget(widget)) {
    return <SmartViewWidgetSettingsComponent key={widget.widgetId} widget={widget} onSave={handleSave} />;
  }
  if (isFilterWidget(widget)) {
    return (
      <FilterWidgetSettingsComponent
        key={`${widget.widgetId}:${filters.length > 0 || dashboardLoaded}`}
        widget={widget}
        onSave={handleFilterSave}
      />
    );
  }

  return <div>Unknown widget type: {JSON.stringify(widget)}</div>;
};

export default WidgetSettings;
